/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from 'contexts';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Page, Tabs, message } from 'components';
import { useBreakpoint } from 'hooks';
import { PopulatedUser } from 'api/UserController/getProfile';
import { CachedOrg } from '@bb/automaton';
import { getProfile } from 'api/UserController';
import { debounce } from 'lodash';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { SupraLockboxActivity, lockboxesActivityPageTabs } from './types';
import LockboxActivityTable from './LockboxActivityTable';
import LockboxesActivityRightHeader from './LockboxesActivityRightHeader';
import errorReporter from '../../../app/errorReporter';
import { getSupraLockboxActivities } from '../../../api/SupraLockboxController';
import { LockboxesActivityFilterOptions } from '../../../api/get/getListingAccessActivities';

const LockBoxesActivityPage = () => {
  const { auth } = useContext(AuthContext);
  const { md } = useBreakpoint();
  const { userId } = useParams<Record<string, string>>();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [lockboxActivities, setLockboxActivities] = useState<
    SupraLockboxActivity[]
  >([]);

  const [user, setUser] = React.useState<PopulatedUser>();
  const [selectedTab, setSelectedTab] = useState<lockboxesActivityPageTabs>(
    auth.org.type === 'mlsStaff' ? 'User' : 'Lockboxes',
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [initialTotal, setInitialTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<LockboxesActivityFilterOptions>({
    ownerOrAssignee: [],
    ownerIds: [],
    assignee: [],
    lockboxIds: [],
    activities: [],
    lockboxSerialNumbers: [],
    startDate: null,
    endDate: null,
    userIds: [],
    listingIds: [],
    keyTypes: [],
  });
  const [sortOptions, setSortOptions] = useState<{
    sort: string;
    order: 'descend' | 'ascend';
  }>({ sort: '', order: 'descend' });
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (userId) {
          // eslint-disable-next-line no-underscore-dangle
          const _user = await getProfile(userId, { populateOrg: true });
          setUser(_user);
        }
      } catch (err) {
        errorReporter.error(
          `Error fetching user ${userId} on LockboxesActivityPage`,
        );
        message.error('Error fetching user');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  useEffect(() => {
    if (search) {
      // eslint-disable-next-line no-underscore-dangle
      const _lockboxIds = searchParams
        .get('lockboxIds')
        ?.split(',')
        .filter(Boolean);
      setFilters(prev => ({
        ...prev,
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate(),
        lockboxIds: _lockboxIds,
      }));
    }
  }, [search, searchParams]);

  useEffect(() => {
    const _userIds = searchParams.get('userIds')?.split(',').filter(Boolean);
    if ((userId || _userIds) && selectedTab) {
      const emptyFilters: LockboxesActivityFilterOptions = {
        ownerOrAssignee: [],
        ownerIds: [],
        assignee: [],
        lockboxIds: [],
        activities: [],
        lockboxSerialNumbers: [],
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate(),
        userIds: [],
        listingIds: [],
        keyTypes: [],
      };
      // resetting filters and initial count/activities on tab change
      if (selectedTab === 'Lockboxes') {
        setFilters({ ...emptyFilters, ownerOrAssignee: _userIds || [userId] });
      } else {
        setFilters({ ...emptyFilters, userIds: _userIds || [userId] });
      }
      setSortOptions({ sort: '', order: 'descend' });
      setPage(1);
      setInitialTotal(0);
    }
  }, [userId, selectedTab, searchParams]);

  useEffect(() => {
    (async () => {
      try {
        if (
          filters.ownerOrAssignee?.length ||
          filters.userIds?.length ||
          filters.lockboxIds?.length
        ) {
          setLoading(true);
          const { activities, count } = await getSupraLockboxActivities({
            filters,
            sort: sortOptions,
            page,
          });
          setLockboxActivities(activities);
          setTotal(count);
          if (!initialTotal || initialTotal < count) setInitialTotal(count);
        }
      } catch (err) {
        message.error('Something went wrong');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, filters, sortOptions]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce(text => {
        setFilters(prev => ({ ...prev, search: text }));
      }, 300),
    [filters],
  );

  React.useEffect(() => () => {
      debouncedSearch.cancel();
    }, []);

  useEffect(() => {
    debouncedSearch(searchText);
  }, [searchText]);

  const breadcrumbRoutes = useMemo(() => {
    const _lockboxIds = searchParams
      .get('lockboxIds')
      ?.split(',')
      .filter(Boolean);
    const _userIds = searchParams.get('userIds')?.split(',').filter(Boolean);
    // No breadcrumbs for non MLS Admins
    if (auth.org.type !== 'mlsStaff') {
      return [];
    }
    if (userId) {
      return [
        { breadcrumbName: 'Users', path: '/mls/users' },
        {
          breadcrumbName: `${user?.firstName} ${user?.lastName}`,
          path: `/mls/user/${userId}`,
        },
        {
          breadcrumbName: 'Lockbox Activity',
          path: `/mls/user/${userId}/lockboxactivity`,
        },
      ];
    }
    if (_lockboxIds?.length) {
      return [
        { breadcrumbName: 'Lockboxes', path: '/mls/lockboxes' },
        {
          breadcrumbName: `Lockbox Activity`,
          path: `/mls/lockboxesActivities${search}`,
        },
      ];
    }
    if (_userIds?.length) {
      return [
        { breadcrumbName: 'Users', path: '/mls/users' },
        {
          breadcrumbName: `Lockbox Activity`,
          path: `/mls/lockboxesActivities${search}`,
        },
      ];
    }
  }, [
    searchParams,
    auth.org.type,
    userId,
    user?.firstName,
    user?.lastName,
    search,
  ]);

  const tabPanelList = useMemo<
    {
      key: lockboxesActivityPageTabs;
      label: lockboxesActivityPageTabs;
      children: React.ReactElement;
    }[]
  >(
    () => [
      {
        key: 'User',
        label: 'User',
        children: (
          <LockboxActivityTable
            filters={filters}
            sortOptions={sortOptions}
            setFilters={setFilters}
            setSortOptions={setSortOptions}
            activities={lockboxActivities}
            loading={loading}
            totalCount={total}
            page={page}
            setPage={setPage}
            tab="User"
          />
        ),
      },
      {
        key: 'Lockboxes',
        label: 'Lockboxes',
        children: (
          <LockboxActivityTable
            filters={filters}
            sortOptions={sortOptions}
            setFilters={setFilters}
            setSortOptions={setSortOptions}
            activities={lockboxActivities}
            loading={loading}
            totalCount={total}
            page={page}
            setPage={setPage}
          />
        ),
      },
    ],
    [filters, sortOptions, lockboxActivities, loading, total, page],
  );
  // Remove Users Tab if not MLS Admin
  if (auth.org.type !== 'mlsStaff') {
    tabPanelList.splice(
      tabPanelList.findIndex(x => x.key.valueOf() === 'User'),
      1,
    );
  }

  return (
    <Page
      type="filled"
      title={'Activity'}
      fillScreenHeight
      showBackButton
      overTitle={<Breadcrumbs breadcrumbRoutes={breadcrumbRoutes} />}
      headerExtra={
        <LockboxesActivityRightHeader
          filters={filters}
          sort={sortOptions}
          page={page}
          setPage={setPage}
          total={total}
          initialTotal={initialTotal}
          searchText={searchText}
          setSearchText={setSearchText}
          setFilters={setFilters}
          isLoading={loading}
        />
      }
    >
      {userId || searchParams.get('userIds')?.split(',').filter(Boolean) ? (
        <Tabs
          activeKey={selectedTab}
          onTabClick={(key: lockboxesActivityPageTabs) => setSelectedTab(key)}
          tabBarStyle={{ margin: 0, padding: `0 ${md ? 20 : 10}px` }}
          tabBarGutter={md ? 32 : 20}
          destroyInactiveTabPane
          items={tabPanelList}
        />
      ) : (
        <LockboxActivityTable
          filters={filters}
          sortOptions={sortOptions}
          setFilters={setFilters}
          setSortOptions={setSortOptions}
          activities={lockboxActivities}
          loading={loading}
          totalCount={total}
          page={page}
          setPage={setPage}
        />
      )}
    </Page>
  );
};

export default LockBoxesActivityPage;
